import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Plus, Trash2, FlaskConical, Building2, User, Package, Edit3, BookOpen, Search, Boxes } from 'lucide-react';
import { api } from '@/shared/api/axios';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Spinner } from '@/shared/components/ui/Spinner';
import { SearchableSelect } from '@/shared/components/ui/SearchableSelect';
import { useProducts, type ProductHierarchyItem } from '@/features/catalog/hooks/useCatalogApi';
import { useProductSearch } from '@/features/pos/hooks/usePosApi';
import { ProductHierarchySelector } from '@/features/catalog/components/ProductHierarchySelector';
import { abbreviatePackaging, derivePackagingType } from '@/shared/utils/packagingAbbreviation';
import { usePipelineList } from '@/features/pipeline/hooks/usePipeline';
import { ROUTES } from '@/routes/paths';
import { useSampleMutations, useSampleRequest } from '../api';
import type { CreateSamplePayload, SampleLine } from '../types';

/** GET /api/pipeline/:id — the record plus its Contact snapshot. */
interface PipelineRecordWithContact {
  name?: string;
  title?: string;
  business_name?: string;
  phone?: string;
  email?: string;
  address?: string;
  contact?: {
    name?: string;
    company?: string;
    phone?: string;
    email?: string;
    address?: string;
    city?: string;
    state?: string;
    pin?: string;
    country?: string;
    // The Contact's dedicated Billing Address — distinct from the legacy flat
    // fields above, which predate the billing/shipping split (db-mongo.js).
    billingAddress?: { street?: string; city?: string; state?: string; zipCode?: string; country?: string } | null;
  } | null;
}

interface FormSampleLine extends Omit<SampleLine, 'productName'> {
  productName: string;
  isManual?: boolean;
}

// RBM's own packaging convention (client-confirmed): up to 5 L -> Box,
// 7.5-26 L -> Bucket, 50/55 L -> Can, ~210 L -> Barrel. See
// packagingTypeFromQuantity() in packagingAbbreviation.ts for the same rule
// applied to a real product's pack size; these are just preset labels for a
// sample line with no catalogue product behind it.
const PACK_SIZE_PRESETS = [
  '100 ml Box',
  '200 ml Box',
  '500 ml Box',
  '1 L Box',
  '5 L Box',
  '20 L Bucket',
  '210 L Barrel',
  'Custom',
];

/**
 * New / edit a sample request — a PAGE, in the app shell.
 *
 * This was a modal, opened from three places. It is a long form: a pipeline
 * record to link, four customer fields and an unbounded list of product lines
 * with their own pack sizes and notes. In a dialog that meant an inner
 * scrollbar over a dimmed page, with the record picker's own dropdown opening
 * inside it. On a page the form has the room it needs, the browser's back
 * button means what it says, and the URL can be sent to a colleague.
 *
 *   /sampling/new                — blank
 *   /sampling/new?recordId=<id>  — pre-linked, from a pipeline record's panel
 *   /sampling/:id/edit           — an existing request
 */
export function SampleFormPage() {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const presetRecordId = searchParams.get('recordId') || '';
  const cloneKey = searchParams.get('clone');
  const presetClientName = searchParams.get('clientName') || searchParams.get('name') || '';
  const presetPhone = searchParams.get('phone') || '';
  const presetEmail = searchParams.get('email') || '';
  const presetAddress = searchParams.get('address') || '';

  const isEdit = Boolean(editId);
  const { data: sampleToEdit, isLoading: loadingSample } = useSampleRequest(editId || null);
  const { createSample, updateSample } = useSampleMutations();

  /** Back to wherever this was opened from, falling back to the Sampling list. */
  const close = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(ROUTES.sampling);
  };

  // ── Product Search & Quick Add — same picker the pipeline record uses ────
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showHierarchy, setShowHierarchy] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsSearching(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // THE WHOLE CATALOGUE, not itemType 'sample' only. This used to restrict
  // the picker to dedicated sample SKUs — its own pack size, its own stock,
  // never a sellable product in a small bottle — so a real sale item could
  // never be sent out as a "free sample" by one mis-click. Confirmed with
  // the client that this restriction is being dropped deliberately: reps
  // need to send any real product as a sample, and the sample form should
  // work exactly like the pipeline record's own product picker, which
  // offers everything. Stock/ledger already post correctly for any product
  // id (postMovement in api-sampling/sample-invoice.js does not read
  // itemType), so nothing downstream needed to change for this.
  const { data: browseData, isLoading: isBrowseLoading } = useProducts({ limit: 30 });
  const { data: searchResults = [], isLoading: isSearchLoading } = useProductSearch(searchQuery);
  const isBrowsing = searchQuery.trim().length === 0;
  const searchListResults = isBrowsing ? (browseData?.data ?? []) : searchResults;
  const searchListLoading = isBrowsing ? isBrowseLoading : isSearchLoading;

  // One picker over the unified pipeline, not a separate Deal list and Lead
  // list — a record is one thing, whichever stage it happens to be at.
  //
  // NARROWED, because the full list is hundreds of records and most of them
  // are never the answer here: `open` drops Closed Won and Closed Lost (a
  // settled deal is not waiting on a sample), and `mine` leaves a rep with
  // their own work. Both are enforced server-side; `mine` is resolved from the
  // session and ignored for roles that already see everything, so a manager
  // still gets the whole team's records.
  const { data: pipelineData } = usePipelineList({ open: true, mine: true, limit: 200, offset: 0 });
  const records = pipelineData?.items || [];

  // Form State
  const [selectedRecordId, setSelectedRecordId] = useState<string>(presetRecordId);
  /** Label for a linked record the narrowed list no longer contains. */
  const [linkedRecordLabel, setLinkedRecordLabel] = useState('');
  const [fetchingContact, setFetchingContact] = useState(false);

  const [customerName, setCustomerName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  // Structured shipping address, same street/city/state/pincode split used on
  // the Quotation/Proforma/Invoice form (QuotationFormPage) and RBM Document
  // Studio, so a printed sample invoice's Ship To box is filled the same way
  // everywhere. Joined back into the single `shippingAddress` string the
  // sample-request API already accepts — no backend change needed. On load,
  // an existing flat address goes into the street field (city/state/pincode
  // can't be reliably split back out of free text) rather than being lost.
  const [shipStreet, setShipStreet] = useState('');
  const [shipCity, setShipCity] = useState('');
  const [shipState, setShipState] = useState('');
  const [shipZip, setShipZip] = useState('');

  const [lines, setLines] = useState<FormSampleLine[]>([
    { productId: '', productName: '', packSize: '200 ml Box', quantity: 1, unitPrice: 0, discountPercent: 0, notes: '', isManual: false },
  ]);

  // Sync state on open/edit
  useEffect(() => {
    if (cloneKey) return;
    if (sampleToEdit) {
      const dId = typeof sampleToEdit.dealId === 'object' ? sampleToEdit.dealId?._id : sampleToEdit.dealId;
      const lId = typeof sampleToEdit.leadId === 'object' ? sampleToEdit.leadId?._id : sampleToEdit.leadId;
      setSelectedRecordId(dId || lId || '');
      setLinkedRecordLabel(sampleToEdit.customerName || '');
      setCustomerName(sampleToEdit.customerName || '');
      setPhone(sampleToEdit.phone || '');
      setEmail(sampleToEdit.email || '');
      setShippingAddress(sampleToEdit.shippingAddress || '');
      setShipStreet(sampleToEdit.shippingAddress || '');
      setShipCity(''); setShipState(''); setShipZip('');
      if (sampleToEdit.lines && sampleToEdit.lines.length > 0) {
        setLines(
          sampleToEdit.lines.map((l) => ({
            productId: l.productId || '',
            productName: l.productName || '',
            sku: l.sku || '',
            packSize: l.packSize || '200 ml Box',
            quantity: l.quantity || 1,
            unitPrice: l.unitPrice || 0,
            discountPercent: l.discountPercent || 0,
            notes: l.notes || '',
            isManual: !l.productId,
          })),
        );
      }
    } else {
      setSelectedRecordId(presetRecordId);
      setLinkedRecordLabel('');
      setCustomerName(presetClientName);
      setPhone(presetPhone);
      setEmail(presetEmail);
      setShippingAddress(presetAddress);
      setShipStreet(presetAddress);
      setShipCity(''); setShipState(''); setShipZip('');
      setLines([{ productId: '', productName: '', packSize: '200 ml Box', quantity: 1, unitPrice: 0, discountPercent: 0, notes: '', isManual: false }]);
    }
  }, [sampleToEdit, presetRecordId, presetClientName, presetPhone, presetEmail, presetAddress]);

  // Arriving from a pipeline record's own panel (?recordId=): fill the customer
  // block straight away, exactly as picking that record by hand would.
  useEffect(() => {
    if (presetRecordId && !isEdit && !cloneKey) void fillFromRecord(presetRecordId);
    // Runs for the id in the URL; picking a different record by hand goes
    // through handleRecordChange instead.
  }, [presetRecordId, isEdit]);

  const [cloneLoading, setCloneLoading] = useState(!!cloneKey);
  const [cloneError, setCloneError] = useState('');
  useEffect(() => {
    if (!cloneKey || !presetRecordId) return;
    let cancelled = false;
    api.get(`/api/pipeline/${presetRecordId}/customer-history?clone=${encodeURIComponent(cloneKey)}`)
      .then(({ data }) => {
        if (cancelled) return;
        const d = data.order;
        setSelectedRecordId(presetRecordId);
        setCustomerName(d.customerName || d.customer?.company || d.customer?.name || '');
        setPhone(d.phone || ''); setEmail(d.email || ''); setShippingAddress(d.shippingAddress || '');
        setShipStreet(d.shippingAddress || ''); setShipCity(''); setShipState(''); setShipZip('');
        setLines(d.lines.map((l: any) => ({
          ...l,
          productId: l.productId || '',
          isManual: !l.productId,
          unitPrice: Number(l.unitPrice) || 0,
          // A cloned quotation/order line carries `discount`/`discountType`,
          // not `discountPercent` — same rupee-vs-percent trap as elsewhere:
          // a rupee-amount discount predates the percent convention and this
          // form has no way to express it, so it is left at 0 rather than
          // misread as a percentage.
          discountPercent: l.discountType === 'percent' ? Number(l.discount) || 0 : Number(l.discountPercent) || 0,
        })));
      })
      .catch(() => { if (!cancelled) setCloneError('Could not load the sample to clone. Go back and try again.'); })
      .finally(() => { if (!cancelled) setCloneLoading(false); });
    return () => { cancelled = true; };
  }, [cloneKey, presetRecordId]);

  /**
   * Fill the customer block from a pipeline record.
   *
   * THE ADDRESS COMES FROM THE CONTACT. This used to read `record.address`,
   * and that field is empty on effectively every record — the street, city,
   * state and PIN live on the linked Contact, which is also where a quotation
   * reads its bill-to from. So the address box stayed blank however complete
   * the client's details were, and someone retyped an address the system
   * already had.
   *
   * GET /api/pipeline/:id returns that Contact as a nested snapshot, so one
   * request answers the whole block. Name, phone and email come from the same
   * place for the same reason — one source, and a sample and a quotation for
   * the same client then carry the same details. The record's own values are
   * the fallback for a record with no Contact behind it yet.
   *
   * Everything filled here stays EDITABLE: a sample often ships to a plant or
   * a lab, not to the address the customer is invoiced at.
   */
  const fillFromRecord = async (recordId: string) => {
    if (!recordId) return;
    setFetchingContact(true);
    try {
      const { data: record } = await api.get<PipelineRecordWithContact>(`/api/pipeline/${recordId}`);
      const c = record?.contact ?? null;

      const name = c?.company || c?.name || record?.business_name || record?.title || record?.name || '';
      if (name) {
        setCustomerName(name);
        setLinkedRecordLabel(name);
      }
      setPhone(c?.phone || record?.phone || '');
      setEmail(c?.email || record?.email || '');

      const ship = (record as any)?.shippingAddress || (c as any)?.shippingAddress;
      const billing = (record as any)?.billingAddress || (c as any)?.billingAddress;
      const hasShip = !!(ship && (ship.street || ship.city || ship.state || ship.zipCode));
      const hasBilling = !!(billing && (billing.street || billing.city || billing.state || billing.zipCode));
      const chosen = hasShip ? ship : (hasBilling ? billing : null);
      const street = chosen ? chosen.street : (c?.address || record?.address);
      const city = chosen ? chosen.city : c?.city;
      const state = chosen ? chosen.state : c?.state;
      const zip = chosen ? chosen.zipCode : c?.pin;
      const country = chosen ? chosen.country : c?.country;

      // One line, because the form has one box. Blank parts drop out rather
      // than leaving ", , 411051" behind.
      const address = [street, city, state, zip, country]
        .map((part) => String(part || '').trim())
        .filter(Boolean)
        .join(', ');
      setShippingAddress(address || record?.address || '');
      // The Contact already carries city/state/pin (or billingAddress'
      // street/city/state/zipCode) as separate fields — use them directly in
      // the structured Ship To boxes instead of re-splitting the joined string.
      setShipStreet(street || record?.address || '');
      setShipCity(city || '');
      setShipState(state || '');
      setShipZip(zip || '');

      if (!address && !record?.address) {
        toast('This client has no address in Contacts — type the delivery address below.', { icon: 'ℹ️' });
      }

      // The sample is of the products already on the enquiry: bring them in
      // (one unit each, editable) instead of making the rep pick them again.
      // Only while the form still holds its untouched blank line.
      const recordLines = Array.isArray((record as any)?.lines) ? (record as any).lines : [];
      if (recordLines.length) {
        setLines((current) => (current.length === 1 && !current[0].productId && !current[0].productName
          ? recordLines.map((l: any) => ({
            productId: l.productId ? String(l.productId) : '',
            productName: l.productName || l.description || '',
            grade: l.grade || undefined,
            packSize: l.packSize || '200 ml Box',
            unit: l.unit || undefined,
            quantity: 1,
            unitPrice: 0,
            discountPercent: 0,
            notes: '',
            isManual: !l.productId,
          }))
          : current));
      }
    } catch {
      // The link itself still stands; only the prefill failed.
      toast.error("Could not read that record's client details. Fill them in below.");
    } finally {
      setFetchingContact(false);
    }
  };

  const handleRecordChange = (recordId: string) => {
    setSelectedRecordId(recordId);
    void fillFromRecord(recordId);
  };

  /** A populated { name } ref, or a bare id, or nothing. */
  const refName = (v: unknown) =>
    (v && typeof v === 'object' && 'name' in (v as any) ? String((v as any).name || '') : '');

  /**
   * Add a product from the catalogue — via the search dropdown OR the RBM
   * Product Selector, same single entry point the pipeline record's picker
   * uses (addProductToLines in ProductLinesEditor). Picking a product
   * already on the list adds to its quantity rather than opening a second
   * line for the same thing; otherwise it fills the single still-blank
   * starter row this form always opens with, rather than leaving that empty
   * row behind as a stray line the first time anything is picked.
   */
  const addCatalogProduct = (product: any, qty = 1, customRate?: number) => {
    const existingIndex = lines.findIndex((l) => l.productId && l.productId === product._id);
    if (existingIndex >= 0) {
      const updated = [...lines];
      updated[existingIndex] = {
        ...updated[existingIndex],
        quantity: (Number(updated[existingIndex].quantity) || 0) + qty,
      };
      setLines(updated);
      setSearchQuery('');
      setIsSearching(false);
      setShowHierarchy(false);
      toast.success(`Increased quantity of ${product.name}.`);
      return;
    }

    const newLine: FormSampleLine = {
      productId: product._id,
      productName: product.name || '',
      sku: product.sku || '',
      // Copied onto the line, not looked up later — see the comment on
      // SampleLine. The catalogue may be reorganised; what was sent may not.
      division: refName(product.divisionId),
      group: refName(product.groupId),
      grade: refName(product.gradeId) || product.grade || '',
      packSize: refName(product.packSizeId) || product.packSize || '200 ml Box',
      // Packaging first — a product's real pack (Box/Drum/Barrel/Bucket/Can)
      // is what the catalogue actually stores per-product, while unitId is a
      // rarely-set generic UoM reference (ML/LTR/KG/GM).
      unit: abbreviatePackaging(derivePackagingType(product)) || refName(product.unitId) || product.unitId?.shortName || '',
      quantity: qty,
      // A custom rate from the RBM Product Selector wins; otherwise the
      // product's own list price, same as every other create form.
      unitPrice: customRate !== undefined ? customRate : (Number(product.sellingPrice) || Number(product.mrp) || 0),
      discountPercent: 0,
      notes: '',
      isManual: false,
    };

    const isBlankStarter = lines.length === 1 && !lines[0].productId && !lines[0].productName.trim() && !lines[0].isManual;
    setLines(isBlankStarter ? [newLine] : [...lines, newLine]);
    setSearchQuery('');
    setIsSearching(false);
    setShowHierarchy(false);
    toast.success(`Added ${product.name}.`);
  };

  /** ProductHierarchySelector's onSelectProduct — same shape as onPick above. */
  const handleHierarchySelect = (item: ProductHierarchyItem, qty: number, customRate?: number) => {
    addCatalogProduct(item, qty, customRate);
  };

  const handleToggleManual = (index: number) => {
    const updated = [...lines];
    const current = updated[index];
    const nextManual = !current.isManual;
    updated[index] = {
      ...current,
      isManual: nextManual,
      productId: nextManual ? '' : current.productId,
    };
    setLines(updated);
  };

  const handleLineChange = (index: number, field: keyof FormSampleLine, value: any) => {
    const updated = [...lines];
    updated[index] = { ...updated[index], [field]: value };
    setLines(updated);
  };

  // Catalogue products are now added via the search bar / RBM Product
  // Selector above the table, which fills the whole line in one click — so
  // this button's job is a genuinely custom/trial item that has no catalogue
  // entry, and opens straight into the manual name field rather than a dead
  // per-row picker with nothing left for it to offer.
  const addLine = () => {
    setLines([
      ...lines,
      { productId: '', productName: '', packSize: '200 ml Box', quantity: 1, unitPrice: 0, discountPercent: 0, notes: '', isManual: true },
    ]);
  };

  /** Qty x Rate, less the line discount — the reference value shown per row. */
  const lineValue = (l: FormSampleLine) => {
    const gross = (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0);
    return gross - gross * ((Number(l.discountPercent) || 0) / 100);
  };
  const totalQty = lines.reduce((sum, l) => sum + (Number(l.quantity) || 0), 0);
  const totalValue = lines.reduce((sum, l) => sum + lineValue(l), 0);

  const removeLine = (index: number) => {
    if (lines.length <= 1) {
      toast.error('A sample request must have at least one product.');
      return;
    }
    setLines(lines.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (cloneLoading || cloneError) {
      toast.error(cloneError || 'Please wait for the source sample to load.');
      return;
    }

    if (!customerName.trim()) {
      toast.error('Please enter the customer / company name.');
      return;
    }

    if (!isEdit && !selectedRecordId) {
      toast.error('Please link this sample to a pipeline record.');
      return;
    }

    // Validate lines
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (!line.isManual && !line.productId && !line.productName.trim()) {
        toast.error(`Please select or enter a product for line item ${i + 1}.`);
        return;
      }
      if (line.isManual && !line.productName.trim()) {
        toast.error(`Please enter the product name for line item ${i + 1}.`);
        return;
      }
      if (!line.packSize.trim()) {
        toast.error(`Please specify the pack size for line item ${i + 1}.`);
        return;
      }
      if (Number(line.quantity) <= 0) {
        toast.error(`Quantity must be at least 1 for line item ${i + 1}.`);
        return;
      }
    }

    const payload: CreateSamplePayload = {
      // Both set to the same id — a Lead and a Deal are the same collection
      // now, so this keeps every existing ?dealId=/?leadId= filter working
      // without the form having to ask which one it "really" is.
      dealId: selectedRecordId || null,
      leadId: selectedRecordId || null,
      customerName: customerName.trim(),
      phone: phone.trim(),
      email: email.trim(),
      // The structured street/city/state/pincode boxes win when filled in;
      // shippingAddress itself only survives as a fallback for a record that
      // was never touched after loading (e.g. an edit where nothing in the
      // Ship To section changed).
      shippingAddress: [shipStreet.trim(), [shipCity.trim(), shipZip.trim()].filter(Boolean).join(' '), shipState.trim()]
        .filter(Boolean).join(', ') || shippingAddress.trim(),
      lines: lines.map((l) => ({
        productId: l.isManual ? null : (l.productId || null),
        productName: l.productName.trim(),
        sku: l.sku?.trim() || '',
        packSize: l.packSize.trim(),
        quantity: Number(l.quantity) || 1,
        unitPrice: Number(l.unitPrice) || 0,
        discountPercent: Number(l.discountPercent) || 0,
        notes: l.notes?.trim() || '',
      })),
    };

    if (isEdit && sampleToEdit) {
      updateSample.mutate(
        { id: sampleToEdit._id, payload },
        {
          onSuccess: () => {
            toast.success('Sample request updated.');
            close();
          },
          onError: (err: any) => {
            toast.error(err?.response?.data?.error || 'Failed to update sample request.');
          },
        },
      );
    } else {
      createSample.mutate(payload, {
        onSuccess: (created) => {
          toast.success(`Sample draft ${created.code} created successfully.`);
          close();
        },
        onError: (err: any) => {
          toast.error(err?.response?.data?.error || 'Failed to create sample request.');
        },
      });
    }
  };

  const isSubmitting = createSample.isPending || updateSample.isPending;

  if ((isEdit && loadingSample) || cloneLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <Spinner />
      </div>
    );
  }

  if (cloneError) return <div className="p-8"><p>{cloneError}</p><Button onClick={close}>Back</Button></div>;

  return (
    <div className="">
      <div className="mx-auto w-full max-w-5xl space-y-6">
        <header className="flex items-center gap-3 border-b border-border pb-4">
          <button
            type="button"
            onClick={close}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-card text-on-surface-variant shadow-xs transition-all hover:bg-muted hover:text-on-surface"
            title="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-on-surface">
              {isEdit ? 'Edit Sample Request' : 'New Lubricant Sample Request'}
            </h1>
            <p className="text-xs font-semibold text-on-surface-variant">
              {isEdit ? sampleToEdit?.code : 'Link it to a pipeline record, then add the sample packs'}
            </p>
          </div>
        </header>

        <form onSubmit={handleSubmit} className="space-y-6">
        {/* Step 1: Pipeline Association */}
        <div className="bg-muted p-4 rounded-xl border border-border">
          <h4 className="text-xs font-bold text-on-surface uppercase tracking-wider flex items-center gap-1.5 mb-3">
            <Building2 className="w-3.5 h-3.5 text-info-text" /> 1. Link to Pipeline
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-on-surface-variant mb-1">
                Select Record <span className="text-destructive">*</span>
              </label>
              {/* SearchableSelect, not a native <select>: the search box lives
                  INSIDE the dropdown, so one control both filters and picks.
                  There are hundreds of records and several clients appear more
                  than once, so the option's second line carries the stage and
                  the value — searched as well as shown, which is how you tell
                  two "xyz company" rows apart. */}
              <SearchableSelect
                value={selectedRecordId}
                onChange={handleRecordChange}
                options={[
                  // The record this sample is already linked to may sit outside
                  // the narrowed list — closed since, or somebody else's. Its
                  // own option is kept so an edit never silently unlinks it.
                  ...(selectedRecordId && !records.some((r) => r.id === selectedRecordId)
                    ? [{
                        value: selectedRecordId,
                        label: linkedRecordLabel || 'Currently linked record',
                        description: 'Already linked to this sample',
                      }]
                    : []),
                  ...records.map((r) => ({
                    value: r.id,
                    label: r.business_name || r.title || r.name || 'Untitled record',
                    description: [
                      r.contact_name || (r.business_name ? r.name : ''),
                      r.stage?.replace(/_/g, ' '),
                      r.value ? `₹${(r.value || 0).toLocaleString('en-IN')}` : '',
                    ].filter(Boolean).join(' · '),
                  })),
                ]}
                placeholder="-- Choose a pipeline record --"
                searchPlaceholder="Type a client or company name…"
                emptyText="No open record matches that."
                aria-label="Pipeline record"
                className="text-xs"
              />
              {fetchingContact && (
                <p className="mt-1 text-data-xs font-medium text-on-surface-variant">Fetching the client's details…</p>
              )}
            </div>
          </div>
        </div>

        {/* Step 2: Customer & Shipping Contact */}
        <div>
          <h4 className="text-xs font-bold text-on-surface uppercase tracking-wider mb-3 flex items-center gap-1.5">
            <User className="w-3.5 h-3.5 text-info-text" /> 2. Customer & Delivery Address
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-on-surface-variant mb-1">
                Recipient / Company Name <span className="text-destructive">*</span>
              </label>
              <Input
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="e.g. Acme Polymers Ltd."
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-on-surface-variant mb-1">Phone Number</label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 98765 43210"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-on-surface-variant mb-1">Email Address</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="contact@company.com"
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-semibold text-on-surface-variant mb-1">
                Dispatch / Factory Shipping Address
              </label>
              {/* Same street/city/state/pincode split as the Quotation/Proforma/
                  Invoice form and RBM Document Studio's Ship To box, so a
                  printed sample invoice fills identically everywhere. Filled
                  from the client's Contact, and deliberately left editable: a
                  sample usually goes to a plant or a lab, not to the address
                  the customer is invoiced at. */}
              <div className="flex flex-col gap-2">
                <Input
                  value={shipStreet}
                  onChange={(e) => setShipStreet(e.target.value)}
                  placeholder="Street / plant address"
                />
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <Input value={shipCity} onChange={(e) => setShipCity(e.target.value)} placeholder="City" />
                  <Input value={shipState} onChange={(e) => setShipState(e.target.value)} placeholder="State" />
                  <Input value={shipZip} onChange={(e) => setShipZip(e.target.value)} placeholder="Pincode" />
                </div>
              </div>
              <p className="mt-1 text-data-xs text-on-surface-variant">
                Filled from the client's Contact — change it if the sample ships somewhere else.
              </p>
            </div>
          </div>
        </div>

        {/* Step 3: Sample Product Lines */}
        <div>
          <h4 className="text-xs font-bold text-on-surface uppercase tracking-wider flex items-center gap-1.5 mb-3">
            <Package className="w-3.5 h-3.5 text-info-text" /> 3. Sample Products &amp; Pack Sizes
          </h4>

          {/* ── Product Search & Quick Add Bar — the exact picker the pipeline
              record uses (ProductLinesEditor), so a rep does not learn a
              second way to find a product depending on which screen they are
              on. Picking a product here fills a whole line in one click;
              "Add Custom Line" is for a genuine trial item with no catalogue
              entry at all. ── */}
          <div className="flex flex-col sm:flex-row items-stretch gap-3 mb-3">
            <div ref={searchContainerRef} className="relative flex-1">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-subtle" />
                <input
                  type="text"
                  className="h-10 w-full rounded-xl border border-border bg-card pl-9 pr-9 text-xs text-on-surface placeholder:text-on-surface-subtle focus:border-info/30 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  placeholder="Search product from catalog by name, SKU, or specification…"
                  value={searchQuery}
                  onFocus={() => setIsSearching(true)}
                  onChange={(e) => { setSearchQuery(e.target.value); setIsSearching(true); }}
                />
                {searchListLoading && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-info/30 border-t-transparent" />
                  </div>
                )}
              </div>

              {isSearching && (
                <div className="absolute z-30 mt-1.5 max-h-96 w-full overflow-y-auto rounded-xl border border-border bg-card p-1 shadow-xl">
                  {isBrowsing && searchListResults.length > 0 && (
                    <div className="px-3 py-1.5 text-data-xs font-medium text-on-surface-subtle">
                      Browse the catalog, or type to search…
                    </div>
                  )}
                  {searchListResults.length === 0 && !searchListLoading ? (
                    <div className="p-4 text-center text-xs text-on-surface-subtle">
                      {isBrowsing ? 'No products in the catalog yet.' : `No products found for "${searchQuery}".`}
                    </div>
                  ) : (
                    searchListResults.slice(0, isBrowsing ? 30 : 10).map((product: any) => (
                      <button
                        key={product._id}
                        type="button"
                        onClick={() => addCatalogProduct(product)}
                        className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left transition hover:bg-muted focus:bg-muted focus:outline-none"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-xs font-semibold text-on-surface">{product.name}</span>
                            {product.sku && (
                              <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-data-xs text-on-surface-variant">
                                {product.sku}
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-data-xs text-on-surface-subtle">
                            {product.categoryId?.name && <span>{product.categoryId.name}</span>}
                            {product.packSizes?.[0]?.label && <span>· {product.packSizes[0].label}</span>}
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            <Button
              type="button"
              onClick={() => setShowHierarchy(!showHierarchy)}
              className={`shrink-0 h-10 gap-1.5 text-xs font-bold ${
                showHierarchy
                  ? 'bg-primary text-white ring-2 ring-primary/30'
                  : 'bg-card hover:bg-muted border border-border text-info-text'
              }`}
            >
              <Boxes className="h-3.5 w-3.5" /> {showHierarchy ? 'Hide Product Selector' : 'RBM Product Selector'}
            </Button>

            <Button
              type="button"
              variant="secondary"
              onClick={addLine}
              className="shrink-0 h-10 text-xs gap-1"
            >
              <Plus className="w-3.5 h-3.5" /> Add Custom Line
            </Button>
          </div>

          {showHierarchy && (
            <div className="mb-3">
              <ProductHierarchySelector
                onSelectProduct={handleHierarchySelect}
                onCancel={() => setShowHierarchy(false)}
              />
            </div>
          )}

          <div className="space-y-3">
            {lines.map((line, index) => (
              <div
                key={index}
                className="p-3.5 bg-muted border border-border rounded-xl relative group transition-all hover:border-border"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-on-surface-variant">
                    Product Item #{index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleToggleManual(index)}
                    className="inline-flex items-center gap-1 text-data-xs font-bold text-info-text hover:text-info-text bg-info/10 hover:bg-info/10 px-2 py-0.5 rounded-md transition-colors"
                  >
                    {line.isManual ? (
                      <>
                        <BookOpen className="w-3 h-3" /> Select from Catalogue
                      </>
                    ) : (
                      <>
                        <Edit3 className="w-3 h-3" /> Enter Custom / Manual Product
                      </>
                    )}
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                  {/* Product Field: Catalogue Select OR Manual Input */}
                  <div className="md:col-span-5">
                    <label className="block text-data-xs font-semibold text-on-surface-variant mb-1">
                      {line.isManual ? 'Custom Product / Trial Name' : 'Catalogue Product'} <span className="text-destructive">*</span>
                    </label>

                    {line.isManual ? (
                      <Input
                        value={line.productName}
                        onChange={(e) => handleLineChange(index, 'productName', e.target.value)}
                        placeholder="e.g. RBM Hydro-68 Premium Trial"
                        className="h-9 text-xs bg-card font-medium"
                        required
                      />
                    ) : line.productId ? (
                      // Picked from the catalogue via the search bar / RBM
                      // Product Selector above — shown read-only here, same
                      // pattern the pipeline record's own line table uses for
                      // a catalogue-sourced line. To pick a different product,
                      // use the toggle above to switch to Custom, or remove
                      // this line and add the right one from the top.
                      <div className="space-y-1">
                        <div className="flex h-9 items-center rounded-lg border border-border bg-muted px-2.5 text-xs font-semibold text-on-surface">
                          {line.productName}
                        </div>
                        {/* What was picked, at a glance: two grades of the same
                            lubricant are otherwise indistinguishable. */}
                        {(line.division || line.group || line.grade || line.unit) && (
                          <p className="text-data-xs italic text-on-surface-variant">
                            {[line.division, line.group, line.grade, line.packSize, line.unit]
                              .filter(Boolean).join(' · ')}
                          </p>
                        )}
                      </div>
                    ) : (
                      // A line with nothing picked yet — reached only by
                      // toggling a Custom line back to Catalogue. Nothing to
                      // pick FROM here any more; the search bar above is the
                      // one place that adds a catalogue product now.
                      <p className="flex h-9 items-center rounded-lg border border-dashed border-border px-2.5 text-data-xs text-on-surface-subtle">
                        Use the search bar or RBM Product Selector above to add a product
                      </p>
                    )}
                  </div>

                  {/* Pack Size Variant */}
                  <div className="md:col-span-3">
                    <label className="block text-data-xs font-semibold text-on-surface-variant mb-1">
                      Pack Size <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="text"
                      list={`pack-sizes-${index}`}
                      value={line.packSize}
                      onChange={(e) => handleLineChange(index, 'packSize', e.target.value)}
                      placeholder="e.g. 200 ml Box"
                      className="w-full h-9 px-2.5 rounded-lg border border-border text-xs bg-card focus:ring-2 focus:ring-blue-500 focus:outline-none font-medium"
                      required
                    />
                    <datalist id={`pack-sizes-${index}`}>
                      {PACK_SIZE_PRESETS.map((p) => (
                        <option key={p} value={p} />
                      ))}
                    </datalist>
                  </div>

                  {/* Quantity */}
                  <div className="md:col-span-2">
                    <label className="block text-data-xs font-semibold text-on-surface-variant mb-1">
                      Qty <span className="text-destructive">*</span>
                    </label>
                    <Input
                      type="number"
                      min={1}
                      value={line.quantity}
                      onChange={(e) => handleLineChange(index, 'quantity', parseInt(e.target.value, 10) || 1)}
                      className="h-9 text-xs bg-card font-medium"
                      required
                    />
                  </div>

                  {/* Delete Line */}
                  <div className="md:col-span-2 flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeLine(index)}
                      className="h-9 px-2.5 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Reference value — what this sample would have cost at list
                    price. Never a charge: it carries no GST and never reaches
                    the SampleInvoice raised on dispatch (see db-sampling.js).
                    Auto-filled from the catalogue pick above; editable for a
                    manual line or a special-priced sample. */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end mt-2">
                  <div className="md:col-span-3">
                    <label className="block text-data-xs font-semibold text-on-surface-variant mb-1">
                      Rate (₹ / unit)
                    </label>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={line.unitPrice ?? 0}
                      onChange={(e) => handleLineChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                      className="h-9 text-xs bg-card font-medium"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-data-xs font-semibold text-on-surface-variant mb-1">
                      Disc %
                    </label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      step="0.01"
                      value={line.discountPercent ?? 0}
                      onChange={(e) => handleLineChange(index, 'discountPercent', parseFloat(e.target.value) || 0)}
                      className="h-9 text-xs bg-card font-medium"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-data-xs font-semibold text-on-surface-variant mb-1">
                      Reference Value
                    </label>
                    <div className="flex h-9 items-center rounded-lg border border-border bg-muted px-2.5 text-xs font-bold text-on-surface-variant">
                      ₹{lineValue(line).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>

                {/* Additional manual fields / notes */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                  {line.isManual && (
                    <div>
                      <Input
                        value={line.sku || ''}
                        onChange={(e) => handleLineChange(index, 'sku', e.target.value)}
                        placeholder="Grade / Viscosity / Trial Code (e.g. ISO VG 46)"
                        className="h-8 text-data-xs bg-card"
                      />
                    </div>
                  )}
                  <div className={line.isManual ? 'md:col-span-1' : 'md:col-span-2'}>
                    <Input
                      value={line.notes || ''}
                      onChange={(e) => handleLineChange(index, 'notes', e.target.value)}
                      placeholder="Specific test instructions or viscosity note (optional)"
                      className="h-8 text-data-xs bg-card"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Totals — reference only, same "not a charge" rule as each line's
              own Reference Value above. */}
          <div className="mt-3 flex flex-wrap items-center justify-end gap-x-8 gap-y-1 rounded-xl border border-border bg-muted px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-on-surface-variant">Total Quantity:</span>
              <span className="text-sm font-bold text-on-surface">{totalQty}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-on-surface-variant">Total Reference Value:</span>
              <span className="text-sm font-bold text-on-surface">
                ₹{totalValue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={close} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting} className="gap-2">
            {isSubmitting ? (
              <>
                <Spinner className="h-4 w-4" /> Saving...
              </>
            ) : (
              <>
                <FlaskConical className="w-4 h-4" />
                {isEdit ? 'Update Sample Request' : 'Create Sample Request'}
              </>
            )}
          </Button>
        </div>
        </form>
      </div>
    </div>
  );
}

export default SampleFormPage;
